import React, { memo, Fragment } from 'react'
import PropTypes from 'prop-types'
import { links, string, sentry, constants, object, isNotEmpty } from 'common/helpers'
import { FormattedHTMLMessage } from 'sb-react-intl'

import Link from 'components/ui/Link/Link'
import Image from 'components/ui/Image/Image'
import UpchargePrice from 'components/UpchargePrice/UpchargePrice'

import messages from './messages'
import s from './SearchResult.scss'


const getLinkByProductType = (type, gender) => {
  const typeValue = type.trim().toLowerCase()
  const linkItem = constants.productTypeLinks.find(({ values }) => values.includes(typeValue))

  if (!linkItem) {
    sentry.captureException(`productTypeLink is not defined for type='${typeValue}'`)

    return
  }

  const { baseUrl, baseMenUrl, queryTypes } = linkItem
  const url = gender === 'male' && baseMenUrl ? baseMenUrl : baseUrl

  if (queryTypes && queryTypes[typeValue]) {
    return `${url}?type=${queryTypes[typeValue]}`
  }

  return url
}

const SearchResult = ({
  className, result, searchValue, isDesktop, gender, unbindListeners, bindListeners,
  onItemClick, onBrandLinkClick, onAddButtonClick, onBestsellersLinkClick, controlsId, descendantId,
}) => {
  const { items, brand, isBestsellers, isExactBrandMatch, isExactTypeMatch, brands } = result

  const productTypeLink = isExactTypeMatch ? getLinkByProductType(searchValue, gender) : false
  const isProductTypeLinkVisible = Boolean(productTypeLink)
  const isBrandItemVisible       = Boolean(isExactBrandMatch || brand)
  const searchValueCapitalized   = string.capitalize(searchValue)
  const searchBrand              = object.get(brand, 'slug', string.kebabCaseify(searchValue))
  const isNotEmptyBrands         = isNotEmpty(brands)

  const isTopLinkVisible = (
    !isNotEmptyBrands
    && (
      isBestsellers
      || isBrandItemVisible
      || isProductTypeLinkVisible
    )
  )

  let brandLink     = links.brand.replace(':slug', searchBrand)
  let topLinkProps  = {}

  if (brand) {
    if (brand.available) {
      brandLink = links.brand.replace(':slug', searchBrand)
    }
    else {
      brandLink = `${links.searchResult}?brand=${brand.id}`
    }
  }

  if (isBestsellers) {
    topLinkProps = {
      title: messages.checkBestsellers,
      to: links.subscription.bestsellers,
      onClick: onBestsellersLinkClick,
    }
  }
  else if (isBrandItemVisible) {
    topLinkProps = {
      title: {
        ...messages.seeAllFragrances,
        values: { slug: searchValueCapitalized },
      },
      to: brandLink,
      onClick: () => onBrandLinkClick(brand),
    }
  }
  else if (isProductTypeLinkVisible) {
    topLinkProps = {
      title: {
        ...messages.seeAllType,
        values: { type: searchValueCapitalized },
      },
      to: productTypeLink,
    }
  }

  const isNewDesktopBrandLinkVisible = (
    isDesktop
    && isBrandItemVisible
    && !isBestsellers
    && !isNotEmptyBrands
  )

  return (
    <div
      id={controlsId}
      className={className}
      onClick={(event) => event.stopPropagation()}
      role="listbox"
      tabIndex="-1"
    >
      <Fragment>
        {
          isNotEmptyBrands && brands
            .filter((brand) => brand.available)
            .map((brand, index) => (
              <Link
                key={brand.slug}
                className={s.topLink}
                blackColor
                withHtmlTitle
                title={{
                  ...messages.seeAllType,
                  values: { type: brand.name },
                }}
                to={links.brand.replace(':slug', brand.slug)}
                onClick={() => onBrandLinkClick(brand)}
                id={`${descendantId}_brand_${brand.slug}`}
                role="option"
                tabIndex={index === 0 ? 0 : -1}
              />
            ))
        }
      </Fragment>
      {
        isNewDesktopBrandLinkVisible ? (
          <Link
            className={s.newBrandLink}
            blackColor
            withHtmlTitle
            to={topLinkProps.to}
            onClick={() => onBrandLinkClick(brand)}
            id={`${descendantId}_brand`}
            role="option"
            tabIndex={0}
          >
            <div className={s.title} aria-hidden="true">{searchValueCapitalized}</div>
            <FormattedHTMLMessage className={s.subTitle} {...topLinkProps.title} />
          </Link>
        ) : (
          isTopLinkVisible && (
            <Link
              className={s.topLink}
              blackColor
              withHtmlTitle={!isBestsellers}
              {...topLinkProps}
              id={`${descendantId}_topLink`}
              role="option"
              tabIndex={0}
            />
          )
        )
      }
      {
        // TODO change item data format
        items.map((item, index) => {
          const { id, name, img, url, available, brand, upchargePrice } = item

          return (
            <div
              key={id}
              className={s.item}
              data-testid={constants.testIds.common.productItem}
              role="none"
            >
              <Link
                key={id}
                className={s.item}
                to={url}
                blackColor
                onClick={() => onItemClick({ item, position: index + 1 })}
                id={`${descendantId}_${index}`}
                role="option"
                tabIndex={index === 0 ? 0 : -1}
              >
                <div className={s.imageContainer}>
                  {
                    Boolean(upchargePrice) && (
                      <UpchargePrice className={s.upchargePrice} price={upchargePrice} small />
                    )
                  }
                  <Image className={s.preview} src={img} h={25} mH={50} alt="" />
                </div>
                <div>
                  <div className={s.title}>{name}</div>
                  {
                    Boolean(brand) && (
                      <div className={s.subTitle}>{brand}</div>
                    )
                  }
                </div>
              </Link>
              {
                Boolean(available) && (
                  <div
                    className={s.addButton}
                    onMouseEnter={unbindListeners}
                    onMouseLeave={bindListeners}
                    data-testid={constants.testIds.common.addToQueueBtn}
                    onClick={() => onAddButtonClick(item)}
                  />
                )
              }
            </div>
          )
        })
      }
    </div>
  )
}

SearchResult.propTypes = {
  className: PropTypes.string,
  result: PropTypes.object,
  searchValue: PropTypes.string,
  isDesktop: PropTypes.bool,
  onItemClick: PropTypes.func,
  onBrandLinkClick: PropTypes.func,
  onAddButtonClick: PropTypes.func,
  onBestsellersLinkClick: PropTypes.func,
  unbindListeners: PropTypes.func,
  bindListeners: PropTypes.func,
}


export default memo(SearchResult)
